import React from 'react';
import { ExternalLink, Loader2, Users } from 'lucide-react';
import { Pill } from './badges';

const Block = ({ title, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">{title}</p>
    <div className="text-xs text-slate-700">{children}</div>
  </div>
);

export const ContactsList = ({ contacts, emptyText = 'No contacts on file.' }) => {
  if (!contacts || contacts.length === 0) return <p className="text-xs text-slate-500">{emptyText}</p>;
  return (
    <table className="w-full text-xs">
      <tbody>
        {contacts.map((c, i) => (
          <tr key={i} className="border-t border-slate-100" data-testid="contact-row">
            <td className="py-1 pr-2 font-medium text-slate-800 whitespace-nowrap">{c.name || '—'}</td>
            <td className="py-1 pr-2 text-slate-600">{c.designation || '—'}{c.bd_role && <span className="ml-1 text-[9px] font-bold text-emerald-700">BD</span>}</td>
            <td className="py-1 pr-2 text-slate-600 break-all">{c.email || (c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">LinkedIn</a> : '—')}</td>
            <td className="py-1 text-[10px] text-slate-400 whitespace-nowrap">{c.source}{c.dnc && <span className="ml-1 text-rose-600 font-bold">DNC</span>}{c.email_status && <span className="ml-1 text-rose-600">{c.email_status}</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const ResearchPanel = ({ research, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 py-2" data-testid="research-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Researching on the web (Perplexity) — usually 20–60s…
      </div>
    );
  }
  if (!research) return null;
  const modes = ['deep', 'availability', 'contacts'].filter(m => research[m]);
  if (!modes.length) return null;
  return (
    <div className="space-y-3" data-testid="research-panel">
      {modes.map(m => {
        const r = research[m];
        return (
          <div key={m} className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {m === 'deep' ? 'Deep research' : m === 'availability' ? 'Partnering availability check' : 'BD contact search'} · Web
              </span>
              <span className="text-[10px] text-slate-400">{(r.researched_at || '').slice(0, 10)}</span>
              {r.relevance && <Pill value={r.relevance} className="ml-auto" />}
            </div>
            {r.summary && <p className="text-xs text-slate-700 leading-relaxed">{r.summary}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(r.manufacturing || r.manufacturing_detail) && (
                <Block title="Manufacturing"><Pill value={r.manufacturing} /> <span className="ml-1">{r.manufacturing_detail}</span></Block>
              )}
              {(r.partnering || r.partnering_detail) && (
                <Block title="Partnering availability"><Pill value={r.partnering} /> <span className="ml-1">{r.partnering_detail}</span></Block>
              )}
            </div>
            {r.relevant_products_platforms?.length > 0 && (
              <Block title="Relevant products / platforms">
                <ul className="list-disc pl-4 space-y-0.5">
                  {r.relevant_products_platforms.map((p, i) => <li key={i}><span className="font-medium">{p.name}</span> <span className="text-slate-400">({p.type}{p.stage ? ` · ${p.stage}` : ''})</span>{p.note ? ` — ${p.note}` : ''}</li>)}
                </ul>
              </Block>
            )}
            {(r.bd_contacts?.length > 0 || r.crm_contacts) && (
              <Block title={<span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> BD contacts</span>}>
                {r.crm_contacts?.length > 0 && <><p className="text-[10px] text-slate-400 mb-1">On file in ForesightRx</p><ContactsList contacts={r.crm_contacts} /></>}
                {r.bd_contacts?.length > 0 && (
                  <>
                    <p className="text-[10px] text-slate-400 mt-2 mb-1">Publicly named (web) — verify before use</p>
                    <ContactsList contacts={r.bd_contacts.map(c => ({ ...c, source: c.source || 'Web' }))} />
                  </>
                )}
                {!r.bd_contacts?.length && !r.crm_contacts?.length && <p className="text-xs text-slate-500">No named BD contacts identified.</p>}
              </Block>
            )}
            {r.recent_news?.length > 0 && (
              <Block title="Recent news">
                <ul className="space-y-0.5">{r.recent_news.slice(0, 5).map((n, i) => <li key={i}><span className="text-slate-400">{n.date}</span> {n.url ? <a href={n.url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{n.headline}</a> : n.headline}</li>)}</ul>
              </Block>
            )}
            {r.risks && <Block title="Risks / unknowns">{r.risks}</Block>}
            {r.source_urls?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {r.source_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5 hover:underline max-w-[260px] truncate">
                    <ExternalLink className="w-2.5 h-2.5 shrink-0" />{u.replace(/^https?:\/\//, '')}
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
